<template>
  <ClientOnly>
    <div class="flex items-center">
      <!-- Comparison icon outside the input box -->
      <span v-if="isComparisonSet" class="mr-2 flex items-center">
        <font-awesome-icon :icon="['far', 'fa-circles-overlap']" />
      </span>

      <!-- Full input + caret group -->
      <div
        class="w-[308px] h-10 inline-flex rounded"
        :class="[
          showBlankNameError
            ? 'border border-danger-600'
            : 'outline outline-1 outline-offset-[-1px] outline-accent-200',
        ]"
      >
        <!-- Input Field -->
        <div class="flex items-center pl-3 pr-2.5 py-2.5 flex-grow">
          <el-tooltip
            :disabled="isTooltipDisabled"
            :content="selection.name"
            :show-after="300"
            placement="bottom-start"
            :trigger-keys="[]"
            popper-class="saved-search-tooltip"
          >
            <input
              ref="savedSearchInputRef"
              v-model="selection.name"
              :data-value="selection.name"
              type="text"
              :placeholder="t('savedSearch.enterNewSearchName')"
              class="w-full text-sm font-medium text-gray-700 text-ellipsis leading-tight bg-transparent border-none focus:outline-none placeholder:text-gray-600 placeholder:[font-size:14px]"
              @mouseenter="checkIfTruncated"
              @input="handleNameChange($event.target.value)"
            />
          </el-tooltip>
        </div>

        <!-- This is the popover trigger: only the caret -->
        <SearchPopover
          ref="searchPopover"
          :custom-class="'z-[9999]'"
          :custom-dropdown-class="'w-[296px] max-h-[284px]'"
          :placeholder-text="t('savedSearch.searchPlaceholder')"
          :dropdown-items="savedSearches"
          :placement="'bottom-end'"
          :selected-item="selection"
          @selected="(item) => (selection = item)"
        >
          <div
            :class="[
              'w-[32px] py-2.5 flex items-center justify-center cursor-pointer border-l rounded-tr rounded-br',
              showBlankNameError && 'border-danger-600',
            ]"
          >
            <font-awesome-icon
              :icon="['fas', 'caret-down']"
              class="text-gray-500 text-[10px]"
              data-intercom-target="search title caret"
            />
          </div>

          <!-- Footer slot -->
          <template #dropdown-footer>
            <PrimaryButton
              v-if="showNewSearchButton"
              variant="outline-secondary-neutral"
              data-target="New Search"
              custom-class="w-full py-2 mt-4 rounded-md text-[14px]"
              :leading-icon="['far', 'plus']"
              @on-click="handleNewSearchClicked"
            >
              {{ t("savedSearch.newSearch") }}
            </PrimaryButton>
          </template>
        </SearchPopover>
      </div>
    </div>
  </ClientOnly>
</template>
<script setup lang="ts">
import { computed, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";
import { ComparisonSets } from "~/types/SearchStateType";
import SearchPopover from "~/components/shared/SearchPopover.vue";
import PrimaryButton from "~/components/shared/primary-button/PrimaryButton.vue";

const { t } = useI18n();

const props = defineProps({
  selectedSearch: {
    type: Object,
    default: () => {},
  },
  savedSearches: {
    type: Array as () => ComparisonSets,
    default: () => [],
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
  showBlankNameError: { type: Boolean, default: false },
  isComparisonSet: { type: Boolean, default: false },
  showNewSearchButton: { type: Boolean, default: true },
});

const emit = defineEmits(["selected", "update", "new-search"]);

const searchPopover: Ref = ref(null);
const savedSearchInputRef = ref(null);
const isTooltipDisabled = ref(true);

const selection = computed({
  get() {
    return props.selectedSearch;
  },
  set(item) {
    emit("selected", item);
  },
});

const handleNameChange = (name: String) => {
  emit("update", name);
  checkIfTruncated();
};

const handleNewSearchClicked = () => {
  if (searchPopover.value && searchPopover.value.closePopover) {
    searchPopover.value.closePopover();
    emit("new-search");
  }
};

const checkIfTruncated = () => {
  const el = savedSearchInputRef.value;
  if (el) {
    isTooltipDisabled.value = el.scrollWidth <= el.clientWidth;
  }
};
</script>

<style>
.saved-search-tooltip {
  white-space: pre;
}
</style>
